import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// NOT: Backend (ApprovalRequestsController) OData değil REST/ApiResponse'tur
// ([HttpGet] List -> ApiResponse<{ items, pendingCount }>, status query + Take(200)).
// Bu yüzden useC360List (OData $top/$skip/$filter) kullanılamaz; veriyi tek seferde
// çekip filtre/sıralama/sayfalamayı c360 görünümünde istemci tarafında yapıyoruz.
const API_BASE = '/api/approval-requests';

const STATUS_TABS = [
  { key: 'PENDING_CHIEF', label: 'Bekleyenler' },
  { key: 'PENDING_HQ', label: 'HQ Onayında' },
  { key: 'APPROVED', label: 'Onaylanan' },
  { key: 'REJECTED', label: 'Reddedilen' },
  { key: '', label: 'Tümü' },
];

const REQUEST_TYPE_LABELS = {
  NON_ROUTE_VISIT: 'Rota Dışı Ziyaret',
  POINT_OPENING: 'Yeni Nokta Açılışı',
};

const STATUS_LABELS = {
  PENDING_CHIEF: { label: 'Şef Onayı Bekliyor', tone: 'wait' },
  PENDING_HQ: { label: 'HQ Onayı Bekliyor', tone: 'wait' },
  APPROVED: { label: 'Onaylandı', tone: 'ok' },
  REJECTED: { label: 'Reddedildi', tone: 'bad' },
  DRAFT: { label: 'Taslak', tone: 'neutral' },
};

const REQUEST_TYPE_FILTER_OPTIONS = [
  { value: 'NON_ROUTE_VISIT', label: 'Rota Dışı Ziyaret' },
  { value: 'POINT_OPENING', label: 'Yeni Nokta Açılışı' },
];

const STATUS_FILTER_OPTIONS = Object.entries(STATUS_LABELS).map(([value, m]) => ({ value, label: m.label }));

// Tablo kolon filtreleri istemci tarafında uygulanır (REST backend, OData yok).
const COLUMN_FILTER_META = {
  trackingNo: { type: 'text', odataField: 'TrackingNo' },
  requestType: { type: 'select', odataField: 'RequestType', options: REQUEST_TYPE_FILTER_OPTIONS },
  status: { type: 'select', odataField: 'Status', options: STATUS_FILTER_OPTIONS },
  notes: { type: 'text', odataField: 'Notes' },
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  const items = pick(data, 'items', 'Items') ?? [];
  const pendingCount = pick(data, 'pendingCount', 'PendingCount') ?? 0;
  return { items, pendingCount };
}

function statusTone(status) {
  return (STATUS_LABELS[status] || { tone: 'neutral' }).tone;
}

function statusLabel(status) {
  return (STATUS_LABELS[status] || { label: status || '-' }).label;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

function tryPrettyJson(value) {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return String(value);
  }
}

// Bir satırı normalize edip stabil alan adlarına indirgeriz (REST PascalCase/camelCase karışık).
function normalizeRow(row) {
  return {
    id: pick(row, 'id', 'Id'),
    trackingNo: pick(row, 'trackingNo', 'TrackingNo') || '',
    requestType: pick(row, 'requestType', 'RequestType') || '',
    status: pick(row, 'status', 'Status') || '',
    notes: pick(row, 'notes', 'Notes') || '',
    rejectionReason: pick(row, 'rejectionReason', 'RejectionReason') || '',
    payloadJson: pick(row, 'payloadJson', 'PayloadJson') || '',
    createdDate: pick(row, 'createdDate', 'CreatedDate') || null,
    decidedAt: pick(row, 'decidedAt', 'DecidedAt') || null,
    gpsLatitude: pick(row, 'gpsLatitude', 'GpsLatitude'),
    gpsLongitude: pick(row, 'gpsLongitude', 'GpsLongitude'),
  };
}

export function ApprovalRequestsPage() {
  usePageMeta('Onay Talepleri', 'Saha temsilcilerinden gelen rota dışı ziyaret ve yeni nokta açılış talepleri.');
  const { canEdit } = usePageActions();

  const [statusFilter, setStatusFilter] = useState('PENDING_CHIEF');
  const [rawRows, setRawRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('CreatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionBusyId, setActionBusyId] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_BASE}?status=${encodeURIComponent(statusFilter)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const { items, pendingCount: pc } = unwrapResponse(payload);
      setRawRows(Array.isArray(items) ? items.map(normalizeRow) : []);
      setPendingCount(pc);
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRawRows([]);
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  // İstemci tarafı filtre + sıralama (REST backend; OData yok).
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const textMatch = (row) => {
      if (!needle) return true;
      const fields = [row.trackingNo, row.requestType, row.status, row.notes, row.rejectionReason, row.payloadJson];
      return fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const columnMatch = (row) => {
      for (const [colId, filter] of Object.entries(columnFilters)) {
        if (!filter) continue;
        const meta = COLUMN_FILTER_META[colId];
        if (!meta) continue;
        const cell = row[colId];
        if (meta.type === 'select') {
          if (filter.value != null && filter.value !== '' && String(cell) !== String(filter.value)) return false;
        } else {
          const v = (filter.value ?? '').toString().toLocaleLowerCase('tr-TR').trim();
          if (v && !String(cell ?? '').toLocaleLowerCase('tr-TR').includes(v)) return false;
        }
      }
      return true;
    };

    const filtered = rawRows.filter((r) => textMatch(r) && columnMatch(r));

    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'TrackingNo': return row.trackingNo || '';
        case 'RequestType': return row.requestType || '';
        case 'Status': return row.status || '';
        case 'CreatedDate':
        default: return row.createdDate ? new Date(row.createdDate).getTime() : 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rawRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((colId, next) => {
    setColumnFilters((prev) => ({ ...prev, [colId]: next }));
    setCurrentPage(1);
  }, []);
  const clearColumnFilter = useCallback((colId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[colId]; return copy; });
    setCurrentPage(1);
  }, []);

  const handleApprove = async (row) => {
    const id = row?.id;
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/approve`, { method: 'POST', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Onay başarısız (${res.status})`);
      showToast('success', 'Talep onaylandı');
      await load();
    } catch (err) {
      showToast('error', err.message || 'Onay başarısız');
    } finally {
      setActionBusyId(null);
    }
  };

  const openReject = (row) => { setRejectRow(row); setRejectReason(''); };

  const submitReject = async () => {
    if (!rejectRow) return;
    const id = rejectRow.id;
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/reject`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ reason: rejectReason.trim() || null }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Red başarısız (${res.status})`);
      showToast('success', 'Talep reddedildi');
      setRejectRow(null);
      setRejectReason('');
      await load();
    } catch (err) {
      showToast('error', err.message || 'Red başarısız');
    } finally {
      setActionBusyId(null);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('trackingNo', {
      header: 'Takip No', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('requestType', {
      header: 'Talep Tipi', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return REQUEST_TYPE_LABELS[v] || v || '-'; },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return <StatusBadge tone={statusTone(v)}>{statusLabel(v)}</StatusBadge>; },
    }),
    columnHelper.accessor('createdDate', {
      header: 'Tarih', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('notes', {
      header: 'Notlar', meta: { filterType: 'text' }, enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.display({
      id: 'gps', header: 'GPS', enableSorting: false,
      cell: ({ row }) => {
        const lat = row.original.gpsLatitude;
        const lng = row.original.gpsLongitude;
        if (lat == null || lng == null) return <span style={{ color: '#94a3b8' }}>-</span>;
        return (
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--accent, #0073ea)', fontWeight: 700, fontSize: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            Haritada Aç
          </a>
        );
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => {
        const r = row.original;
        const busy = actionBusyId === r.id;
        const canDecide = r.status === 'PENDING_CHIEF';
        return (
          <span className="c360-actions" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={() => setDetailRow(r)}><ViewIcon /></button>
            {canDecide && canEdit && (
              <>
                <button type="button" className="c360-action-btn" style={{ background: '#16a34a', color: '#fff', opacity: busy ? 0.6 : 1 }} disabled={busy} title="Onayla" onClick={() => handleApprove(r)}>{busy ? '...' : 'Onayla'}</button>
                <button type="button" className="c360-action-btn" style={{ background: '#dc2626', color: '#fff', opacity: busy ? 0.6 : 1 }} disabled={busy} title="Reddet" onClick={() => openReject(r)}>Reddet</button>
              </>
            )}
          </span>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [actionBusyId, canEdit]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setCurrentPage(1);
    if (!first) { setSortField('CreatedDate'); setSortDir('desc'); return; }
    setSortField(toODataSortField(first.id));
    setSortDir(first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'trackingNo', headerName: 'Takip No' },
        { field: 'requestType', headerName: 'Talep Tipi', map: (v) => REQUEST_TYPE_LABELS[v] || v || '' },
        { field: 'status', headerName: 'Durum', map: (v) => statusLabel(v) },
        { field: 'createdDate', headerName: 'Tarih', map: (v) => formatDate(v) },
        { field: 'notes', headerName: 'Notlar' },
        { field: 'rejectionReason', headerName: 'Red Gerekçesi' },
      ];
      const exportData = filteredSorted.map((r) => {
        const o = {};
        cols.forEach((c) => { o[c.field] = c.map ? c.map(r[c.field]) : (r[c.field] ?? ''); });
        return o;
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(exportData, cols, `onay-talepleri_${dateStr}`);
      showToast('success', `${exportData.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {STATUS_TABS.map((tab) => {
        const active = statusFilter === tab.key;
        return (
          <button
            key={tab.key || 'all'}
            type="button"
            onClick={() => setStatusFilter(tab.key)}
            style={{ ...(active ? s.btnPrimary : s.btnSecondary), padding: '6px 14px', fontSize: 13 }}
          >
            {tab.label}
          </button>
        );
      })}
      <input
        style={s.filterInput}
        type="search"
        placeholder="Takip no / not / payload..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load} disabled={loading}>{loading ? 'Yükleniyor...' : 'Yenile'}</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '8px 14px', borderRadius: 999,
          background: 'rgba(253, 171, 61, 0.14)', border: '1px solid rgba(253, 171, 61, 0.32)',
          color: '#92400e', fontWeight: 800, fontSize: 13,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--warn)' }} />
          {pendingCount} bekleyen
        </span>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Onay Talepleri"
        subtitle="Saha temsilcilerinden gelen rota dışı ziyaret ve yeni nokta açılış talepleri."
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={setDetailRow}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{detailRow.trackingNo || 'Talep Detayı'}</h3>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Talep Tipi</label>
                  <div style={s.inputView}>{REQUEST_TYPE_LABELS[detailRow.requestType] || detailRow.requestType || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div><StatusBadge tone={statusTone(detailRow.status)}>{statusLabel(detailRow.status)}</StatusBadge></div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Oluşturma</label>
                  <div style={s.inputView}>{formatDate(detailRow.createdDate)}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Karar Tarihi</label>
                  <div style={s.inputView}>{formatDate(detailRow.decidedAt)}</div>
                </div>
              </div>
              {detailRow.notes && (
                <div style={s.formGroup}>
                  <label style={s.label}>Notlar</label>
                  <div style={s.textareaView}>{detailRow.notes}</div>
                </div>
              )}
              {detailRow.rejectionReason && (
                <div style={s.formGroup}>
                  <label style={s.label}>Red Gerekçesi</label>
                  <div style={{ ...s.textareaView, background: 'rgba(226, 68, 92, 0.06)' }}>{detailRow.rejectionReason}</div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Payload (JSON)</label>
                <pre style={{
                  margin: 0, padding: 12, borderRadius: 10,
                  background: '#0f172a', color: '#e2e8f0',
                  fontSize: 12, lineHeight: 1.5,
                  maxHeight: 280, overflow: 'auto',
                }}>
                  {tryPrettyJson(detailRow.payloadJson)}
                </pre>
              </div>
            </div>
            <div style={s.modalFooter}>
              {detailRow.status === 'PENDING_CHIEF' && canEdit && (
                <>
                  <button type="button" style={s.btnDanger} onClick={() => { const r = detailRow; setDetailRow(null); openReject(r); }}>Reddet</button>
                  <button type="button" style={{ ...s.btnPrimary, background: '#16a34a' }} onClick={() => { const r = detailRow; setDetailRow(null); handleApprove(r); }}>Onayla</button>
                </>
              )}
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {rejectRow && (
        <div style={s.overlay} onClick={() => setRejectRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Talebi Reddet</h3>
              <button type="button" style={s.closeBtn} onClick={() => setRejectRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary, #4b5563)' }}>
                <strong>{rejectRow.trackingNo || 'Talep'}</strong> reddedilecek. İsterseniz bir gerekçe ekleyebilirsiniz.
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Red Gerekçesi (opsiyonel)</label>
                <textarea
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Örn. Konum belgelenmemiş, ziyaret amacı net değil…"
                  style={s.textarea}
                />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setRejectRow(null)}>Vazgeç</button>
              <button type="button" style={{ ...s.btnDanger, opacity: actionBusyId ? 0.6 : 1 }} disabled={!!actionBusyId} onClick={submitReject}>
                {actionBusyId ? 'Gönderiliyor...' : 'Reddet'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default ApprovalRequestsPage;
